import { z } from 'zod';

const booleanishSchema = z.union([
  z.boolean(),
  z.literal('true'),
  z.literal('false'),
]);

const normalizeBooleanish = (value: boolean | 'true' | 'false'): boolean =>
  value === true || value === 'true';

/** Query/body flag that may be omitted or sent as boolean or `"true"` / `"false"`. */
export const OptionalBooleanQueryParamSchema = z.optional(
  booleanishSchema.transform(normalizeBooleanish),
);

/** Like {@link OptionalBooleanQueryParamSchema} but defaults to `false` when omitted. */
export const BooleanQueryParamDefaultFalseSchema = z
  .optional(booleanishSchema.transform(normalizeBooleanish))
  .transform((value) => value ?? false);

/** Like {@link OptionalBooleanQueryParamSchema} but defaults to `true` when omitted. */
export const BooleanQueryParamDefaultTrueSchema = z
  .optional(booleanishSchema.transform(normalizeBooleanish))
  .transform((value) => value ?? true);

/** Accepts a string or string[] (e.g. query serialization) and normalizes to `string[]`. */
export const OptionalStringArrayQueryParamSchema = z.optional(
  z.union([z.array(z.string()), z.string()]).transform((value) => {
    if (value === '') {
      return undefined;
    }
    if (typeof value === 'string') {
      return [value];
    }
    return value;
  }),
);

export const createConfidenceFilterQueryParamSchema = <
  const T extends readonly [string, ...string[]],
>(
  values: T,
) => {
  const valueSchema = z.enum(values);
  const _normalizedSchema = z
    .union([valueSchema, z.array(valueSchema)])
    .optional();
  return z.optional(
    z
      .union([valueSchema, z.array(valueSchema), z.string()])
      .transform((value): z.infer<typeof _normalizedSchema> => {
        if (typeof value === 'string') {
          const trimmedValue = value.trim();
          if (!trimmedValue) {
            return undefined;
          }
          if (!trimmedValue.includes(',')) {
            const parsed = valueSchema.safeParse(trimmedValue);
            return parsed.success ? parsed.data : undefined;
          }
          const parsedLevels = trimmedValue
            .split(',')
            .map((level) => level.trim())
            .filter(Boolean)
            .map((level) => valueSchema.safeParse(level))
            .flatMap((result) => (result.success ? [result.data] : []));
          return parsedLevels.length > 0 ? parsedLevels : undefined;
        }
        return value;
      }),
  );
};

export const parseInternalNetworkListQueryBoolean = (
  value: unknown,
): boolean | undefined => {
  if (value === undefined || value === null || value === '') {
    return undefined;
  }
  if (typeof value === 'boolean') {
    return value;
  }
  if (typeof value === 'string') {
    if (value === 'true') {
      return true;
    }
    if (value === 'false') {
      return false;
    }
  }
  return Boolean(value);
};
